import React, { use, useCallback, useEffect, useMemo, useState } from 'react'
import { useWatch, type Control, type FieldValues, type UseFormSetValue } from 'react-hook-form'

import Button from 'components/shared/button'
import Input, { InputType } from 'components/shared/inputs/input'

import useTranslation from 'utils/hooks/useTranslation'

import * as dd from 'constants/dropdown'
import { convertToSelect } from 'utils'
import { useSalesNetwork } from 'utils/hooks/useSalesNetworks'
import { SvgCirclePlus, SvgCommandAdd, SvgSupprimer } from 'components/svg'
import { defaultEmetteurDetail, heatModeSchema } from 'store/heatModeSlice'
import { useAppDispatch } from 'store/hooks'
import _ from 'lodash'
import { useGetFlowTemperatureMutation } from 'store/api'
import { mergeFlowTemperature, setDeperds } from 'store/projectSlice'
import { useSelector } from 'react-redux'

interface HeatModeZoneProps {
  control: Control<any>
  setValue: UseFormSetValue<any>
  zone: any
  name: string
  index: number
  addZone: any
  removeZone: any
  calculateDeperd: any
  onSelect?: (name: keyof FieldValues, code?: string | null, label?: string | null, shouldValidate?: boolean, shouldDirty?: boolean) => void
  getValues?: any | undefined
  setDisableNavigarion?: any | undefined
  calculateDeperdAsync?: any | undefined
  autoCalc?: boolean
}

type Emetteur = {
  value: string
  text: string
  [key: string]: any
}

const HeatModeZone: React.FC<HeatModeZoneProps> = ({
  control,
  name,
  //watch, getValues,
  setValue,
  zone,
  index,
  addZone,
  removeZone,
  calculateDeperd,
  getValues,
  setDisableNavigarion,
  onSelect,
  calculateDeperdAsync,
  autoCalc = true

}) => {
  const { t } = useTranslation()
  const { network } = useSalesNetwork()
  const dispatch = useAppDispatch()
  const [getFlowTemp] = useGetFlowTemperatureMutation()
  const projectStore = useSelector((state: any) => state.project)

  const emetteursFiltered = network?._parameters.TRANSMITTER_CALCUL
    ? dd.getEmetteursByZone(index)
    : dd.getEmetteursByZone(index).filter(
      e =>
        e.label !== dd.EMETTEUR_CODES.DESC_DETAILLEE1 &&
        e.label !== dd.EMETTEUR_CODES.DESC_DETAILLEE2
    )

  const [data, setData] = useState<{
    emetteurs: Emetteur[]
    temperatures: []
  }>({
    emetteurs: convertToSelect(emetteursFiltered),
    temperatures: [],
  })

  const zones = useWatch({ control, name: 'heatMode.zones' })


  useEffect(() => {
    setTemperatureCombo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.emetteurs])

  const setTemperatureCombo = useCallback(() => {
    const selectedEmetteur = data.emetteurs.find((item) => item.value === zone.emetteur) //NOSONAR
    if (selectedEmetteur) {
      zone.emetteur_schemaCode = selectedEmetteur.schemaCode //NOSONAR
      zone.temperatureNeeded = selectedEmetteur.temperatures.length > 0 //NOSONAR

      setValue(`heatMode.${name}.emetteur_schemaCode`, zone.emetteur_schemaCode, { shouldValidate: true, shouldDirty: true }) //?
      setValue(`heatMode.${name}.temperatureNeeded`, zone.temperatureNeeded, { shouldValidate: true, shouldDirty: true }) //?

      // Set les temperature par rapport au type d'emetteur
      // supprime les temperature > 60 pour le type "radiateur" uniquement chez thermor
      setData((prev) => ({
        ...prev,
        temperatures: selectedEmetteur.temperatures
          .filter((t: any) =>
            selectedEmetteur.text !== 'SELECT_Z1_RADIATEURS' && network?._parameters.brand === 'thermorfr' ? +t <= 60 : true,
          )
          .map((temp: string) => ({
            value: temp,
            text: temp,
          })),
      }))
    }


  }, [data.emetteurs, network?._parameters.brand, zone])


  const calcualteFlowTemperatureAsync = async (deperd: any) => {
    setDisableNavigarion(true)
    try {
      const heatModeValues = getValues('heatMode')
      await heatModeSchema.validate(heatModeValues, { abortEarly: false })
      const currentZone = heatModeValues.zones[index]
      const isDetailedZone = (currentZone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE1 || currentZone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE2)
        && !_.isEmpty(currentZone.detail)

      if (isDetailedZone) {
        const expand = (valueFn: (d: any) => any) =>
          currentZone.detail.map((d: any) => {
            const arr = []
            for (let i = 1; i <= d.nombreEmeteur; i++) {
              arr.push(valueFn(d))
            }
            return arr
          })

        const payload = {
          deltaT: 10,
          length: expand((d) => parseFloat(d.length)),
          pmax: deperd || 0,
          radiatorClass: expand((d) => d.radiatorClass),
          radiatorModel: expand((d) => d.radiatorModel),
          setpointTemp: parseInt(heatModeValues.deperditions.temperature),
          width: expand((d) => parseFloat(d.width)),
        }

        const res = await getFlowTemp({ data: payload }).unwrap()
        if (res) {
          onChange(res.minTemperature, `heatMode.zones[${index}].temperature`)
        }
        dispatch(mergeFlowTemperature({ index, data: res }))
      }
    } catch (err) {
      console.error(err)
      onChange(null, `heatMode.${name}.temperature`)
      dispatch(mergeFlowTemperature({ index, data: null }))
    }
    setDisableNavigarion(false)
  }

  const onChange = (e: any, name: string) => {
    setValue(name, e, { shouldValidate: true, shouldDirty: true });
    calculateDeperd()
  }

  const debouncedCalculation = useMemo(
    () =>
      _.debounce(async () => {
        if (!autoCalc) return
        const deperd = await calculateDeperdAsync()
        await calcualteFlowTemperatureAsync(deperd)
      }, 500),
    []
  )

  const onChangeDetail = async (e: any, name: string) => {
    setValue(name, e, { shouldValidate: true, shouldDirty: true })
    debouncedCalculation()
  }


  const onTypeEmetteurChange = useCallback(
    (value?: any) => {
      const code = data.emetteurs.find((item) => item.value === value)?.text as string //NOSONAR
      if (code == dd.EMETTEUR_CODES.DESC_DETAILLEE1 || code == dd.EMETTEUR_CODES.DESC_DETAILLEE2) {
        dispatch(setDeperds(null))
      }

      onSelect?.(`heatMode.${name}.emetteur`, value, code as string) // NOSONAR
      zone.emetteur = value
      zone.temperature = value === '4' ? '55' : (
        value === '1' || value === '2' || value === '3'
          ? '35' : '') //NOSONAR
      setValue(`heatMode.${name}.temperature`, zone.temperature, { shouldValidate: false, shouldDirty: false })
      setTemperatureCombo()
      calculateDeperd()

      if (code !== dd.EMETTEUR_CODES.DESC_DETAILLEE1 && index == 0) {
        dispatch(mergeFlowTemperature({ index, data: null }))
      }
      if (code !== dd.EMETTEUR_CODES.DESC_DETAILLEE2 && index == 1) {
        dispatch(mergeFlowTemperature({ index, data: null }))
      }
    },
    [setValue],
  )

  const onDeleteEmetteurDetail = useCallback((e: React.MouseEvent, zoneIndex: number, detailIndex: number) => {
    e.preventDefault()
    const updatedDetail = zones[zoneIndex].detail?.filter((_: any, i: number) => i !== detailIndex) || []
    setValue(`heatMode.zones.${zoneIndex}.detail`, updatedDetail, { shouldValidate: false, shouldDirty: false })
    debouncedCalculation()
  }, [zones, setValue])

  const onAddEmetteurDetail = (e: React.MouseEvent, zoneIndex: number) => {
    e.preventDefault()
    dispatch(mergeFlowTemperature({ index: zoneIndex, data: null }))

    const updatedDetail = [
      ...(zones[zoneIndex].detail || []),
      { ...defaultEmetteurDetail, id: Date.now().toString() },
    ]
    setValue(`heatMode.zones.${zoneIndex}.detail`, updatedDetail, { shouldValidate: false, shouldDirty: false })
  }
  return (
    <>
      <group data-width="auto" data-direction="column" data-justify="end">
        <label className="form_fields" data-space-bottom="10">
          <div className="data_label">{t('ZONE') + ' ' + (index + 1)}</div>
        </label>
        <group
          data-gap="10"
          data-space="10"
          data-space-left='0'
          //data-background="accent-xtralight"
          data-radius="10"
          data-direction="column"
        >
          <group data-gap="20">
            <group data-length="forcefit" data-fit="1" data-direction="column" data-size="large" data-min-length='280'>
              <Input
                type={InputType.Text}
                label={`${t('ZONE_NAME')} ${index + 1}`}
                control={control}
                name={`heatMode.${name}.name`}
                size="large"
                placeholder={`${t('ZONE_NAME_PH')}`}
                translateLabel={false}
              // onChange={(e) => onChange(e, `heatMode.${name}.name`)}
              />
            </group>
            <group data-length="forcefit" data-fit="1" data-direction="column" data-size="large" data-max-length='275'>
              <Input
                type={InputType.Number}
                min={0}
                max={2000}
                step={0.1}
                label={'ZONE_SURFACE'}
                requiredLabel={true}
                control={control}
                name={`heatMode.${name}.surface`}
                size="large"
                placeholder={`${t('WARMIN_SURFACE_PH')}`}
                onChange={(e) => onChange(e, `heatMode.${name}.surface`)}
              />
            </group>
            <group ddata-length="forcefit" data-fit="1" data-direction="column" data-size="large" data-max-length='275'>
              <Input
                type={InputType.Number}
                label={'ZONE_HEIGHT'}
                min={0}
                max={10}
                step={0.1}
                requiredLabel={true}
                control={control}
                name={`heatMode.${name}.hauteur`}
                size="large"
                //placeholder={`${t('ZONE_HEIGHT')}`}
                onChange={(e) => onChange(e, `heatMode.${name}.hauteur`)}
              />
            </group>
          </group>
          <group data-gap="20">
            <group data-length="forcefit" data-fit="1" data-direction="column" data-size="large">
              <Input
                type={InputType.Select}
                label={'ZONE_EMITTER'}
                requiredLabel={true}
                control={control}
                options={data.emetteurs}
                name={`heatMode.${name}.emetteur`}
                allowSearch={true}
                size="large"
                onChange={onTypeEmetteurChange}
                //placeholder={`${t('ZONE_EMITTER')}`}
                translateValue={true}
              />
            </group>
            {(index === 0 || zone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE1 || zone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE2 || zone.emetteur < 5) ? <group data-length="forcefit" data-fit="1" data-direction="column" data-size="large" data-max-length='275'>
              <Input
                type={InputType.Select}
                label={t('ZONE_TEMPERATURE')}
                translateLabel={false}
                requiredLabel={true}
                control={control}
                options={data.temperatures}
                name={`heatMode.${name}.temperature`}
                disabled={!data.temperatures || data.temperatures.length === 0}
                allowSearch={true}
                size="large"
                //placeholder={`${t('ZONE_TEMPERATURE')}`}
                onChange={(e) => onChange(e, `heatMode.${name}.temperature`)}
              />
            </group> :
              <group data-length="forcefit" data-fit="1" data-direction="column" data-size="large">
              </group>
            }
          </group>
          {(zone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE1 || zone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE2) && <group data-gap="20" data-adaptive="desktop">
            <group data-direction="row" data-width="auto" data-space="10">

              <group data-direction="column" data-gap="20">

                {
                  zones[index].detail?.map((detItem: any, i: number) => {
                    return (
                      <group
                        data-direction="row"
                        data-gap="10"
                        key={detItem.id}
                        data-align={i !== 0 ? "center" : "end"}
                      >

                        <group data-direction="row" data-gap="10" data-align="center">
                          <group
                            data-background="main-color-custom-lighter"
                            data-length="30"
                            data-height="30"
                            data-color="black"
                            data-align="center"
                            data-justify="center"
                            data-text-size="13"
                            data-weight="700"
                            data-radius="40"
                            data-shrink="no"
                            data-margin-vertical="10"
                          >
                            {i + 1}
                          </group>
                          <group data-fit="1" data-direction="column" >
                            <Input
                              label={"CATEGORIE_EMETTEUR"}
                              type={InputType.Select}
                              requiredLabel
                              control={control}
                              options={convertToSelect(dd.categorieEmetteurOptions)}
                              name={`heatMode.${name}.detail[${i}].radiatorClass`}
                              allowSearch={true}
                              size="large"
                              translateValue={true}
                              onChange={(e) => {
                                const radiatorClassValue = typeof e === 'string' ? e : e?.target?.value
                                onChangeDetail(e, `heatMode.${name}.detail[${i}].radiatorClass`)
                                setValue(`heatMode.${name}.detail[${i}].radiatorClass_i18nCode`, dd.getRadiatorClassI18nCode(radiatorClassValue), { shouldValidate: false, shouldDirty: true })
                                onChangeDetail(null, `heatMode.${name}.detail[${i}].radiatorModel`)
                                setValue(`heatMode.${name}.detail[${i}].radiatorModel_i18nCode`, null, { shouldValidate: false, shouldDirty: true })
                              }}
                              showInfoMessage={false}
                            />
                          </group>
                          <group data-fit="1" data-direction="column" data-size="large" >
                            <Input
                              label={"TYPE_EPAISSEUR_EMETTEUR"}
                              type={InputType.Select}
                              requiredLabel
                              control={control}
                              options={zone?.detail?.[i]?.radiatorClass ? convertToSelect(dd.typeEmetteurByCategory[zone.detail[i].radiatorClass]) : []}
                              name={`heatMode.${name}.detail[${i}].radiatorModel`}
                              allowSearch={true}
                              size="large"
                              disabled={!zone?.detail?.[i]?.radiatorClass || zone?.detail?.[i]?.radiatorClass === "Radiateurs sèche serviettes" || zone?.detail?.[i]?.radiatorClass === "Radiateur multi colonnes vertical acier"}
                              translateValue={true}
                              onChange={(e) => {
                                onChangeDetail(e, `heatMode.${name}.detail[${i}].radiatorModel`)
                                setValue(`heatMode.${name}.detail[${i}].radiatorModel_i18nCode`, dd.getTypeEmetteurI18nCode(zone?.detail?.[i]?.radiatorClass, typeof e === 'string' ? e : e?.target?.value), { shouldValidate: false, shouldDirty: true })
                              }}
                              showInfoMessage={false}
                            />
                          </group>
                        </group>
                        <group data-direction="row" data-gap="10" data-align="center" data-length='30'> </group>
                        <group data-fit="1" data-direction="column" data-max-length='120'>
                          <Input
                            label={"LARGEUR_EMETTEUR"}
                            type='number'
                            requiredLabel
                            control={control}
                            options={data.emetteurs}
                            name={`heatMode.${name}.detail[${i}].width`}
                            allowSearch={true}
                            min={0}
                            size="large"
                            translateValue={true}
                            step={(zone?.detail?.[i]?.width === null || zone?.detail?.[i]?.width === undefined) ? 100 : 1}
                            onChange={(value) => {
                              setValue(`heatMode.${name}.detail[${i}].width`, value, { shouldValidate: true, shouldDirty: true })
                              debouncedCalculation()
                            }}
                            showInfoMessage={false}
                          />
                        </group>
                        <group data-fit="1" data-direction="column" data-max-length='120'>
                          <Input
                            label={"HAUTEUR_EMETTEUR"}
                            type='number'
                            step={(zone?.detail?.[i]?.length === null || zone?.detail?.[i]?.length === undefined) ? 30 : 1}
                            min={0}
                            requiredLabel
                            control={control}
                            options={data.emetteurs}
                            name={`heatMode.${name}.detail[${i}].length`}
                            allowSearch={true}
                            size="large"
                            translateValue={true}
                            onChange={(value) => {
                              setValue(`heatMode.${name}.detail[${i}].length`, value, { shouldValidate: true, shouldDirty: true })
                              debouncedCalculation()
                            }}
                            showInfoMessage={false}
                          />
                        </group>
                        <group data-fit="1" data-direction="column" data-max-length='110'>
                          <Input
                            label={"NOMBRE_EMETTEUR"}
                            type={InputType.Select}
                            requiredLabel
                            control={control}
                            options={dd.emetteurCount}
                            name={`heatMode.${name}.detail[${i}].nombreEmeteur`}
                            allowSearch={true}
                            size="large"
                            defaultValue={1}
                            labelSize={10}
                            onChange={(e) => onChangeDetail(e, `heatMode.${name}.detail[${i}].nombreEmeteur`)}
                          />
                        </group>
                        {i !== 0 ? <group data-width="auto" data-margin-bottom="10">
                          <a href="#" data-type="group" data-width="auto" data-gap="5" data-interactive="highlight"
                            onClick={(e: React.MouseEvent) => onDeleteEmetteurDetail(e, index, i)}>
                            <icon data-svg-size="20" data-svg-color="secondary"><SvgSupprimer /></icon>
                          </a>
                        </group> : <></>}
                      </group>
                    )
                  })
                }
                <group>
                  <a href="#" data-type="group" data-width="auto" data-gap="5" data-interactive="highlight"
                    onClick={(e: React.MouseEvent) => onAddEmetteurDetail(e, index)}>
                    <icon data-svg-color="secondary" data-svg-size="20" data-fill=""><SvgCirclePlus /></icon>

                    <text data-text-size="medium" data-ellipsis="" data-color="secondary">{t("ADD_EMETTEUR")}</text>
                  </a>
                </group>
              </group>
            </group>
          </group>}
          {(zone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE1 || zone.emetteur_i18nCode === dd.EMETTEUR_CODES.DESC_DETAILLEE2) && <group data-gap="20" data-adaptive="mobile">
            <group data-direction="row" data-width="auto" data-space="10">

              <group data-direction="column" data-gap="10">
                {
                  zones[index].detail?.map((detItem: any, i: number) => {
                    return (
                      <group data-direction="column" data-gap="10" key={detItem.id}>
                        <group data-fit="1" style={{ border: "1px dashed var(--main-color-custom-lighter)" }}>
                        </group>
                        <group data-fit="1" data-direction="column" data-size="large" >
                          <Input
                            label={`${t("CATEGORIE_EMETTEUR")} ${i + 1}`}
                            type={InputType.Select}
                            requiredLabel={true}
                            control={control}
                            options={convertToSelect(dd.categorieEmetteurOptions)}
                            name={`heatMode.${name}.detail[${i}].radiatorClass`}
                            allowSearch={true}
                            size="large"
                            translateValue={true}
                            translateLabel={false}
                            onChange={(e) => {
                              const radiatorClassValue = typeof e === 'string' ? e : e?.target?.value
                              onChangeDetail(e, `heatMode.${name}.detail[${i}].radiatorClass`)
                              setValue(`heatMode.${name}.detail[${i}].radiatorClass_i18nCode`, dd.getRadiatorClassI18nCode(radiatorClassValue), { shouldValidate: false, shouldDirty: true })
                              onChangeDetail(null, `heatMode.${name}.detail[${i}].radiatorModel`)
                              setValue(`heatMode.${name}.detail[${i}].radiatorModel_i18nCode`, null, { shouldValidate: false, shouldDirty: true })
                            }}
                          />
                        </group>
                        <group data-fit="1" data-direction="column" data-size="large" >
                          <Input
                            label="TYPE_EPAISSEUR_EMETTEUR"
                            type={InputType.Select}
                            requiredLabel={true}
                            control={control}
                            options={zone?.detail?.[i]?.radiatorClass ? convertToSelect(dd.typeEmetteurByCategory[zone.detail[i].radiatorClass]) : []}
                            name={`heatMode.${name}.detail[${i}].radiatorModel`}
                            allowSearch={true}
                            size="large"
                            disabled={!zone?.detail?.[i]?.radiatorClass || zone?.detail?.[i]?.radiatorClass === "Radiateurs sèche serviettes" || zone?.detail?.[i]?.radiatorClass === "Radiateur multi colonnes vertical acier"}
                            translateValue={true}
                            onChange={(e) => {
                              const radiatorModelValue = typeof e === 'string' ? e : e?.target?.value
                              onChangeDetail(e, `heatMode.${name}.detail[${i}].radiatorModel`)
                              setValue(`heatMode.${name}.detail[${i}].radiatorModel_i18nCode`, dd.getTypeEmetteurI18nCode(zone?.detail?.[i]?.radiatorClass, radiatorModelValue), { shouldValidate: false, shouldDirty: true })
                            }}
                          />
                        </group>
                        <group data-gap="10">

                          <group data-fit="1" data-direction="column">
                            <Input
                              label="LARGEUR_EMETTEUR"
                              type='number'
                              requiredLabel={true}
                              control={control}
                              options={data.emetteurs}
                              name={`heatMode.${name}.detail[${i}].width`}
                              allowSearch={true}
                              min={0}
                              size="large"
                              translateValue={true}
                              step={(zone?.detail?.[i]?.width === null || zone?.detail?.[i]?.width === undefined) ? 100 : 1}
                              onChange={(value) => {
                                setValue(`heatMode.${name}.detail[${i}].width`, value, { shouldValidate: true, shouldDirty: true })
                                debouncedCalculation()
                              }}
                            />
                          </group>
                          <group data-fit="1" data-direction="column" >
                            <Input
                              label="HAUTEUR_EMETTEUR"
                              type='number'
                              step={(zone?.detail?.[i]?.length === null || zone?.detail?.[i]?.length === undefined) ? 30 : 1}
                              min={0}
                              requiredLabel={true}
                              control={control}
                              options={data.emetteurs}
                              name={`heatMode.${name}.detail[${i}].length`}
                              allowSearch={true}
                              size="large"
                              translateValue={true}
                              labelSize={10}
                              onChange={(value) => {
                                setValue(`heatMode.${name}.detail[${i}].length`, value, { shouldValidate: true, shouldDirty: true })
                                debouncedCalculation()
                              }}
                            />
                          </group>
                        </group>
                        <group data-fit="1" data-direction="column" >
                          <Input
                            label="NOMBRE_EMETTEUR"
                            type={InputType.Select}
                            requiredLabel={true}
                            control={control}
                            options={dd.emetteurCount}
                            name={`heatMode.${name}.detail[${i}].nombreEmeteur`}
                            allowSearch={true}
                            size="large"
                            defaultValue={1}
                            labelSize={10}
                            onChange={(e) => onChangeDetail(e, `heatMode.${name}.detail[${i}].nombreEmeteur`)}
                          />
                        </group>
                        {i !== 0 ? <a href="#" data-type="group" data-width="auto" data-gap="5" data-interactive="highlight"
                          onClick={(e: React.MouseEvent) => onDeleteEmetteurDetail(e, index, i)}>
                          <icon data-svg-size="20" data-svg-color="secondary"><SvgSupprimer /></icon>
                        </a> : <group data-length="20"></group>}
                      </group>
                    )
                  })
                }
                <group>
                  <a href="#" data-type="group" data-width="auto" data-gap="5" data-interactive="highlight"
                    onClick={(e: React.MouseEvent) => onAddEmetteurDetail(e, index)}>
                    <icon data-svg-color="secondary" data-svg-size="20" data-fill=""><SvgCirclePlus /></icon>

                    <text data-text-size="medium" data-ellipsis="" data-color="secondary">{t("ADD_EMETTEUR")}</text>
                  </a>
                </group>
                {/* <group>
                  {isCalced ? <Button secondary large onClick={() => setIsCalced(false)}>
                    {t("CALCUL_TEMP_DEPART")}
                  </Button> :
                    <group data-direction='row' data-background="main-color-custom-lighter"
                      data-radius="5"
                      data-height="80"
                      data-space='5'
                    >
                      <group data-gap="5" data-align="center" data-direction='row'>
                        <text data-text-size="medium" data-text-align='center'>{`${t("DEPART_TEMP")}: `}</text>
                        <text data-text-size="medium" data-weight='600'>{`XX ${t("°C_UNIT")}`}</text>
                      </group>
                      <group data-fit='1' data-justify="start">
                        <Button secondary simple onClick={() => setIsCalced(true)} text={t("NEW_CALCUL_TEMP_DEPART")}
                          icon={<icon data-svg-size="20" data-svg-color="secondary">directory_sync</icon>}>
                        </Button>
                      </group>
                    </group>
                  }
                </group> */}
              </group>
            </group>
          </group>}
          <group data-gap="20">
            <group data-width="auto" data-direction="column" data-justify="end">
              <space data-height="10"></space>
              {index === 0 && zones?.length < 2 && <Button material secondary large outline text={t('ADD_ZONE')} onClick={addZone} icon={<icon data-svg-size="20" data-svg-color="secondary"><SvgCommandAdd /></icon>} />}
              {index > 0 && <Button material secondary large outline text={t('REMOVE_ZONE')} onClick={() => removeZone(index)} icon={<icon data-svg-size="20" data-svg-color="secondary"><SvgSupprimer /></icon>} />}
            </group>
          </group>
        </group>
      </group >
    </>
  )
}

export default HeatModeZone
